import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";

const TIPS = {
  "/manager/shift-creation": {
    title: "シフト作成の使い方",
    steps: [
      { step: "① 月の準備", body: "休業日と曜日別の必要人数を設定します。休業日は全員 0 人扱いになります。" },
      { step: "② 配置マジック", body: "「◎入れます」の希望者を、希望日数までベストな配置で自動的に下書きします。締め切り後はモードを選んで再実行できます。" },
      { step: "③ 日を調整する", body: "カレンダーから日付をクリックして、入っている人 / 候補スタッフ を見ながら手動調整。確定ボタンで「マジック再実行時も崩れない」状態にできます。" },
      { step: "④ 確定する", body: "最終確認後、全スタッフに公開します。" },
    ],
  },
  "/manager/staff-status": {
    title: "スタッフ別状況の見方",
    steps: [
      { step: "希望 vs 確定", body: "各スタッフの希望日数と実際に確定された日数を一覧できます。" },
      { step: "前月差分", body: "前月の確定日数との差を表示。マイナスの方は次回優先候補としてご検討ください。" },
    ],
  },
  "/manager/messages": {
    title: "メッセージの使い方",
    steps: [
      { step: "スタッフへ送信", body: "個別のスタッフに連絡が送れます。" },
      { step: "未読確認", body: "新着メッセージがあるとサイドバーにバッジが付きます。" },
    ],
  },
  "/manager/change-requests": {
    title: "変更依頼の承認",
    steps: [
      { step: "申請を確認", body: "スタッフからのシフト変更申請をここで承認 / 却下します。" },
    ],
  },
  "/manager/confirmed": {
    title: "確定シフト一覧",
    steps: [
      { step: "確定シフト", body: "公開済みのシフトを月単位で確認できます。" },
    ],
  },
  "/staff": {
    title: "やさまるシフト (スタッフ画面) の使い方",
    steps: [
      { step: "希望を提出", body: "各日 ◎入れます / △必要なら / ×入れません のいずれかで希望を出してください。" },
      { step: "確定シフト確認", body: "公開後、自分のシフトが「マイシフト」に表示されます。" },
    ],
  },
};

const DEFAULT_TIPS = {
  title: "やさまるシフトについて",
  steps: [
    { step: "コンセプト", body: "「希望日数 vs 確定日数」を基本に、無理のない配置をサポートします。" },
    { step: "操作のヒント", body: "各ページの ? ボタンを押すと、ページごとの使い方を案内します。" },
  ],
};

export default function YasamaruHelp() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const popRef = useRef(null);

  // pick matching tip by longest prefix
  const tipKey = Object.keys(TIPS).find((k) => pathname.startsWith(k));
  const tip = tipKey ? TIPS[tipKey] : DEFAULT_TIPS;

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  return (
    <div className="fixed bottom-20 right-5 z-40" data-testid="yasamaru-help">
      {open && (
        <div
          ref={popRef}
          className="absolute bottom-24 right-0 w-80 bg-white rounded-2xl shadow-2xl border border-emerald-200 p-4 animate-in fade-in slide-in-from-bottom-2"
          data-testid="yasamaru-help-popover"
        >
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="absolute top-2 right-2 text-slate-400 hover:text-slate-700"
            aria-label="閉じる"
            data-testid="yasamaru-help-close"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2 mb-2">
            <img src="/yasamaru-guide-128.png" alt="やさまる" className="w-10 h-10 shrink-0" />
            <h4 className="font-bold text-sm text-slate-900">{tip.title}</h4>
          </div>
          <ul className="space-y-2 max-h-80 overflow-y-auto">
            {tip.steps.map((s, i) => (
              <li key={i} className="text-xs">
                <p className="font-bold text-emerald-700 mb-0.5">{s.step}</p>
                <p className="text-slate-600 leading-relaxed">{s.body}</p>
              </li>
            ))}
          </ul>
          <div className="mt-3 pt-2 border-t border-slate-100 text-[10px] text-slate-400 text-center">
            こまったときは、やさまるが助けにいきます 🌱
          </div>
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="yasamaru-help-button"
        aria-label="ヘルプ"
        className="group relative bg-white/60 hover:bg-white border-2 border-emerald-200 hover:border-emerald-400 hover:shadow-xl rounded-full w-12 h-12 hover:w-16 hover:h-16 shadow opacity-60 hover:opacity-100 transition-all duration-200 flex items-center justify-center"
        title="ヘルプ"
      >
        <img src="/yasamaru-guide-128.png" alt="やさまる" className="w-9 h-9 group-hover:w-12 group-hover:h-12 transition-all" />
        <span className="absolute -top-1 -right-1 bg-emerald-500 text-white text-[9px] font-bold w-4 h-4 group-hover:w-5 group-hover:h-5 rounded-full flex items-center justify-center group-hover:bg-emerald-600 transition-all">?</span>
      </button>
    </div>
  );
}
